import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { css } from "@stubhub/broadway-foundation/css";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import type { MappingPagedCollectionOfEventSearchResultAndEventGridItem } from "src/http/types";
import { Content } from "src/shared/components/content";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useCreateQueryFn } from "src/shared/hooks/useCreateQueryFn";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { createQueryKey } from "src/shared/utils/query-helpers";
import { QueryKeyPrefix } from "src/shared/utils/query/query-key-prefixes";
import {
  exponentialBackoffRetryDelay,
  FIVE_RETRIES,
  STALE_TIME_ONE_HOUR,
} from "src/shared/utils/query/retry-constants";
import type { Dates } from "src/types/shared/cookies";
import { GridCardBase } from "src/website-apps/shared/components/grid-card-base";
import { ENTITY_TYPES } from "src/website-apps/shared/components/grid/favorites-fs/constants";
import { CAROUSEL_SIZES, VENUE_CAROUSEL_IDS } from "src/website-apps/shared/components/responsive-carousel/constants";
import { ResponsiveCarousel } from "src/website-apps/shared/components/responsive-carousel/ResponsiveCarousel";

const titleStyles = css({
  marginBlockEnd: "primitives.100",
  textStyle: "title.medium",
  tablet: {
    textStyle: "header.medium",
  },
});

interface Props {
  venueId: number;
  venueName: string | null;
  dates: Maybe<Dates>;
  onHasContent?: () => void;
}

export const VenueTrendingCarousel = ({ venueId, venueName, dates, onHasContent }: Props) => {
  const createQueryFn = useCreateQueryFn();
  const storePageEvent = useStorePageEvent();
  const isPhone = !useMediaQuery(Breakpoint.Tablet);
  const { from, to } = dates?.dates ?? {};
  const fromValue = from ? (typeof from === "string" ? from : from.toJSON()) : undefined;
  const toValue = to ? (typeof to === "string" ? to : to.toJSON()) : undefined;

  const { data: events, isLoading: pending } = useQuery({
    queryKey: createQueryKey(QueryKeyPrefix.TRENDING_EVENTS, { venueId, fromValue, toValue }),
    queryFn: async (context) =>
      createQueryFn<MappingPagedCollectionOfEventSearchResultAndEventGridItem>({
        method: "TrendingEventsLocale",
        params: [
          ["categoryId", 0],
          ["maxRows", 20],
          ["page", 0],
          ["venueId", venueId],
          ...(fromValue ? [["fromDate", fromValue] as [string, string]] : []),
          ...(toValue ? [["toDate", toValue] as [string, string]] : []),
        ],
      })(context),
    enabled: Boolean(venueId),
    staleTime: STALE_TIME_ONE_HOUR,
    retry: FIVE_RETRIES,
    retryDelay: (attemptIndex) => {
      storePageEvent(PE_TYPE.LOGGING, PE_KEYS.VENUE_TRENDING_CAROUSEL_FETCH_RETRY_REQUEST, `ERROR-${attemptIndex + 1}`);
      return exponentialBackoffRetryDelay(attemptIndex);
    },
  });

  const hasEvents = Boolean(events?.items?.length);

  useEffect(() => {
    if (hasEvents) {
      onHasContent?.();
    }
  }, [hasEvents, onHasContent]);

  if (!hasEvents && !pending) {
    return null;
  }

  const title = venueName ? (
    <h2 className={titleStyles}>
      {/* "Trending events" */}
      <Content id="cid$#3577100#" />
    </h2>
  ) : null;

  return (
    <>
      {title}
      <ResponsiveCarousel
        carouselId={VENUE_CAROUSEL_IDS.TRENDING_EVENTS_V2}
        gapPx={16}
        hasHoverEffect={!isPhone}
        hidePaginationButtons={isPhone}
        imageHeightHint={226}
        isLoading={pending}
        items={events?.items ?? []}
        loadingCount={isPhone ? 2 : 4}
        size={isPhone ? CAROUSEL_SIZES.marqueeMobile : CAROUSEL_SIZES.marqueeDesktop}
        useFlex
        renderItem={(item, index) => (
          <GridCardBase
            entityId={item.eventId}
            entityType={ENTITY_TYPES.EVENT}
            event={item}
            eventDate={item.eventDateTimeFormatted?.shortDate ?? undefined}
            eventTime={item.eventDateTimeFormatted?.time ?? undefined}
            imageUrl={item.imageUrl ?? ""}
            onClick={() =>
              storePageEvent(PE_TYPE.CLICK, PE_KEYS.VENUE_TRENDING_CAROUSEL_CLICK, `${item?.eventId ?? ""}-${index}`)
            }
            size={isPhone ? "mobile" : "default"}
            title={item.eventName ?? ""}
            url={item.url ?? ""}
          />
        )}
      />
    </>
  );
};
